import { apiRequest } from "./queryClient";
import { getSupabaseClient } from './supabase';
import type { UserFormData, GeneratedPlan, Exercise } from "../types";
import type { ExerciseVideoFeedback, VideoFeedbackAnalysis } from "@shared/schema";
import { trackEvent } from "./analytics/posthog";

/**
 * Make an authenticated API request with the user's JWT token
 * This helper extracts the Supabase session and includes the Authorization Bearer token
 */
export async function authenticatedRequest(
  method: string,
  url: string,
  data?: unknown
): Promise<Response> {
  const supabase = await getSupabaseClient();
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    try { trackEvent('auth_session_expired', { page_source: window.location.pathname }); } catch {}
    throw new Error('Not authenticated');
  }

  // If token is expired or about to expire (within 30s), refresh it
  let accessToken = session.access_token;
  const expiresAt = session.expires_at; // Unix timestamp in seconds
  if (expiresAt && expiresAt < Math.floor(Date.now() / 1000) + 30) {
    const { data: refreshData } = await supabase.auth.refreshSession();
    if (refreshData.session?.access_token) {
      accessToken = refreshData.session.access_token;
    } else {
      try { trackEvent('auth_session_expired', { page_source: window.location.pathname, reason: 'refresh_failed' }); } catch {}
      throw new Error('Not authenticated');
    }
  }

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${accessToken}`,
  };

  if (data) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: 'include',
  });

  if (!res.ok) {
    let errorMessage = res.statusText;
    try {
      const errorBody = await res.text();
      if (errorBody) {
        try {
          const errorJson = JSON.parse(errorBody);
          errorMessage = errorJson.message || errorBody;
        } catch {
          errorMessage = errorBody;
        }
      }
    } catch (e) {
      errorMessage = res.statusText;
    }
    throw new Error(`${res.status}: ${errorMessage}`);
  }

  return res;
}

export interface VideoSearchResult {
  success: boolean;
  exerciseName: string;
  query: string;
  videoUrl: string | null;
  videoTitle: string | null;
  error?: string;
  errorType?: "API_KEY_MISSING" | "API_ERROR" | "NO_RESULTS" | "RATE_LIMITED";
  source?: "cache" | "serpapi";
}

export interface VideoFeedbackUploadResponse {
  success: boolean;
  feedback?: ExerciseVideoFeedback;
  error?: string;
  errorType?: "API_KEY_MISSING" | "API_ERROR" | "PARSE_ERROR" | "RATE_LIMITED" | "ALREADY_EXISTS";
  existingFeedback?: ExerciseVideoFeedback;
}

export interface VideoFeedbackHistoryResponse {
  success: boolean;
  history: ExerciseVideoFeedback[];
  error?: string;
}

export const api = {
  generatePlan: async (formData: UserFormData & { userId?: string }): Promise<{ planData: GeneratedPlan }> => {
    const response = await apiRequest("POST", "/api/plans/generate", formData);
    return response.json();
  },

  getExerciseByName: async (name: string): Promise<Exercise> => {
    const response = await apiRequest("GET", `/api/exercises/name/${encodeURIComponent(name)}`);
    return response.json();
  },

  getAllExercises: async (): Promise<Exercise[]> => {
    const response = await apiRequest("GET", "/api/exercises");
    return response.json();
  },

  searchExerciseVideo: async (exerciseName: string): Promise<VideoSearchResult> => {
    const response = await apiRequest(
      "GET",
      `/api/exercises/video-search?name=${encodeURIComponent(exerciseName)}`
    );
    return response.json();
  },

  // Video feedback endpoints
  uploadExerciseFeedback: async (
    userId: string,
    trainingPlanId: number,
    dayNumber: number,
    exerciseName: string,
    videoFile: File
  ): Promise<VideoFeedbackUploadResponse> => {
    const supabase = await getSupabaseClient();
    const { data: { session } } = await supabase.auth.getSession();

    const headers: Record<string, string> = {};
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }
    // Don't set Content-Type — browser sets multipart boundary automatically

    const formData = new FormData();
    formData.append('trainingPlanId', trainingPlanId.toString());
    formData.append('dayNumber', dayNumber.toString());
    formData.append('exerciseName', exerciseName);
    formData.append('video', videoFile);

    const response = await fetch('/api/exercise-feedback/upload', {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });
    if (!response.ok) {
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch {
        throw new Error(text || `Upload failed with status ${response.status}`);
      }
    }
    return response.json();
  },

  getExerciseFeedback: async (id: number): Promise<{ success: boolean; feedback?: ExerciseVideoFeedback; error?: string }> => {
    const response = await apiRequest("GET", `/api/exercise-feedback/${id}`);
    return response.json();
  },

  getExerciseFeedbackHistory: async (
    userId: string,
    trainingPlanId: number,
    dayNumber: number,
    exerciseName: string
  ): Promise<VideoFeedbackHistoryResponse> => {
    const params = new URLSearchParams({
      userId,
      trainingPlanId: trainingPlanId.toString(),
      dayNumber: dayNumber.toString(),
      exerciseName,
    });
    const response = await apiRequest("GET", `/api/exercise-feedback/history?${params.toString()}`);
    return response.json();
  },

  deleteExerciseFeedback: async (id: number): Promise<{ success: boolean; error?: string }> => {
    const response = await apiRequest("DELETE", `/api/exercise-feedback/${id}`);
    return response.json();
  },
};
